import type {
  GetV2AdminJotauditDashboardNotesByOrganizationIdApiArg,
  GetV2AdminJotauditDashboardProvidersByOrganizationIdApiArg,
  GetV2AdminJotauditDashboardRulesByRuleIdAuditsDrilldownAndOrganizationIdApiArg,
  GetV2AdminJotauditDashboardRulesByRuleIdStatsAndOrganizationIdApiArg,
  GetV2AdminJotauditDashboardRulesComplianceStatsByOrganizationIdApiArg,
  GetV2AdminJotauditDashboardStatsByOrganizationIdApiArg,
} from '@store/generated/admin';
import { enhancedApi } from '@store/generated/admin';

/**
 * Feature-specific tag types for jotaudit admin
 */
const JOTAUDIT_ADMIN_TAG_TYPES = [
  'NoteAudit',
  'NoteAuditList',
  'Provider',
  'ProviderList',
  'AuditRevision',
  'AuditRevisionList',
  'Organization',
  'OrganizationList',
  'GlobalStats',
] as const;

/**
 * JotAudit Admin API - extends the generated admin API with custom tagging and null filtering
 */
export const jotauditAdminApi = enhancedApi.enhanceEndpoints({
  addTagTypes: JOTAUDIT_ADMIN_TAG_TYPES,
  endpoints: {
    // Override query functions to filter out null values (backend rejects null)
    getV2AdminJotauditDashboardNotesByOrganizationId: {
      query: (queryArg: GetV2AdminJotauditDashboardNotesByOrganizationIdApiArg) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        if (queryArg.page !== undefined) {
          params.page = String(queryArg.page);
        }
        if (queryArg.perPage !== undefined) {
          params.per_page = String(queryArg.perPage);
        }
        if (queryArg.minQualityScore !== undefined && queryArg.minQualityScore !== null) {
          params.min_quality_score = String(queryArg.minQualityScore);
        }
        if (queryArg.maxQualityScore !== undefined && queryArg.maxQualityScore !== null) {
          params.max_quality_score = String(queryArg.maxQualityScore);
        }
        if (queryArg.cptMismatchOnly !== undefined && queryArg.cptMismatchOnly !== null) {
          params.cpt_mismatch_only = String(queryArg.cptMismatchOnly);
        }
        if (queryArg.isCompleted !== undefined && queryArg.isCompleted !== null) {
          params.is_completed = String(queryArg.isCompleted);
        }
        return {
          url: `/v2/admin/jotaudit/dashboard/notes/${queryArg.organizationId}`,
          params,
        };
      },
      providesTags: (result: any) =>
        result && result.audits
          ? [
              ...result.audits.map((item: any) => ({
                type: 'NoteAudit' as const,
                id: item.audit?.note_audit_id || 'unknown',
              })),
              { type: 'NoteAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'NoteAuditList', id: 'LIST' }],
    },
    getV2AdminJotauditDashboardProvidersByOrganizationId: {
      query: (queryArg: GetV2AdminJotauditDashboardProvidersByOrganizationIdApiArg) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        return {
          url: `/v2/admin/jotaudit/dashboard/providers/${queryArg.organizationId}`,
          params,
        };
      },
      providesTags: [{ type: 'ProviderList' as const, id: 'LIST' }],
    },
    getV2AdminJotauditDashboardStatsByOrganizationId: {
      query: (queryArg: GetV2AdminJotauditDashboardStatsByOrganizationIdApiArg) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        return {
          url: `/v2/admin/jotaudit/dashboard/stats/${queryArg.organizationId}`,
          params,
        };
      },
      providesTags: [{ type: 'GlobalStats' as const, id: 'LIST' }],
    },
    getV2AdminJotauditDashboardRulesComplianceStatsByOrganizationId: {
      query: (
        queryArg: GetV2AdminJotauditDashboardRulesComplianceStatsByOrganizationIdApiArg,
      ) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        if (queryArg.ruleIds) {
          params.rule_ids = queryArg.ruleIds;
        }
        if (queryArg.comparisonMode !== undefined && queryArg.comparisonMode !== null) {
          params.comparison_mode = String(queryArg.comparisonMode);
        }
        return {
          url: `/v2/admin/jotaudit/dashboard/rules/compliance-stats/${queryArg.organizationId}`,
          params,
        };
      },
      providesTags: [{ type: 'NoteAuditList' as const, id: 'LIST' }],
    },
    getV2AdminJotauditDashboardRulesByRuleIdStatsAndOrganizationId: {
      query: (
        queryArg: GetV2AdminJotauditDashboardRulesByRuleIdStatsAndOrganizationIdApiArg,
      ) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        return {
          url: `/v2/admin/jotaudit/dashboard/rules/${queryArg.ruleId}/stats/${queryArg.organizationId}`,
          params,
        };
      },
      providesTags: [{ type: 'NoteAuditList' as const, id: 'LIST' }],
    },
    getV2AdminJotauditDashboardRulesByRuleIdAuditsDrilldownAndOrganizationId: {
      query: (
        queryArg: GetV2AdminJotauditDashboardRulesByRuleIdAuditsDrilldownAndOrganizationIdApiArg,
      ) => {
        const params: Record<string, string> = {};
        if (queryArg.complianceStatus) {
          params.compliance_status = queryArg.complianceStatus;
        }
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        if (queryArg.page !== undefined) {
          params.page = String(queryArg.page);
        }
        if (queryArg.perPage !== undefined) {
          params.per_page = String(queryArg.perPage);
        }
        return {
          url: `/v2/admin/jotaudit/dashboard/rules/${queryArg.ruleId}/audits/drilldown/${queryArg.organizationId}`,
          params,
        };
      },
      providesTags: [{ type: 'NoteAuditList' as const, id: 'LIST' }],
    },
    getV2AdminJotauditDashboardNotesDetailByOrganizationIdAndNoteId: {
      providesTags: (result, _error, { noteId }) =>
        result && result.audit
          ? [
              { type: 'NoteAudit' as const, id: result.audit.note_audit_id },
              { type: 'NoteAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'NoteAudit', id: noteId }],
    },
    getV2AdminJotauditDashboardNotesDetailByOrganizationIdAndNoteIdHistory: {
      providesTags: (result, _error, { noteId }) =>
        result
          ? [
              { type: 'AuditRevisionList' as const, id: noteId },
              { type: 'NoteAudit' as const, id: noteId },
            ]
          : [{ type: 'AuditRevisionList', id: noteId }],
    },
    getV2AdminJotauditDashboardNotesDetailByOrganizationIdAndNoteIdRevisionsRevision: {
      providesTags: (result, _error, { noteId, revision }) =>
        result
          ? [
              { type: 'AuditRevision' as const, id: `${noteId}-${revision}` },
              { type: 'NoteAudit' as const, id: noteId },
            ]
          : [{ type: 'AuditRevision', id: `${noteId}-${revision}` }],
    },
  },
});

// Export aliased hooks matching provider pattern
export const {
  useGetV2AdminJotauditDashboardStatsByOrganizationIdQuery: useGetAdminDashboardStatsQuery,
  useGetV2AdminJotauditDashboardProvidersByOrganizationIdQuery: useGetAdminProvidersQuery,
  useGetV2AdminJotauditDashboardNotesByOrganizationIdQuery: useGetAdminNotesQuery,
  useGetV2AdminJotauditDashboardRulesComplianceStatsByOrganizationIdQuery:
    useGetAdminRuleComplianceStatsQuery,
  useGetV2AdminJotauditDashboardRulesByRuleIdStatsAndOrganizationIdQuery:
    useGetAdminRuleDetailStatsQuery,
  useGetV2AdminJotauditDashboardRulesByRuleIdAuditsDrilldownAndOrganizationIdQuery:
    useGetAdminRuleAuditsDrilldownQuery,
  useGetV2AdminJotauditDashboardNotesDetailByOrganizationIdAndNoteIdQuery: useGetAdminNoteDetailQuery,
  useGetV2AdminJotauditDashboardNotesDetailByOrganizationIdAndNoteIdHistoryQuery: useGetAdminNoteHistoryQuery,
  useGetV2AdminJotauditDashboardNotesDetailByOrganizationIdAndNoteIdRevisionsRevisionQuery: useGetAdminNoteRevisionQuery,
  useGetV2AdminJotauditDashboardOrganizationsQuery: useGetAdminOrganizationsQuery,
  useGetV2AdminJotauditDashboardProvidersByOrganizationIdAndUserIdQuery: useGetAdminProviderSummaryQuery,
} = jotauditAdminApi;

